import { Link, useLocation } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { findSectionAndItemByPath } from "../lib/navigation";
import { SUBPAGE_CONTENT } from "../lib/content";
import { Reveal } from "../components/site/Reveal";

const BlockSection = ({ block, index }) => {
  if (block.quote) {
    return (
      <Reveal delay={index * 80}>
        <div className="my-10 md:my-12 text-center">
          <p className="font-serif italic text-charcoal text-2xl md:text-3xl leading-snug max-w-2xl mx-auto">
            <span className="gold-text">{block.quote}</span>
          </p>
        </div>
      </Reveal>
    );
  }
  return (
    <Reveal delay={index * 80}>
      <div className="border-l-2 border-gold pl-6 mb-10">
        {block.title && (
          <p className="overline text-charcoal mb-3">{block.title}</p>
        )}
        {block.body && (
          <p className="text-graphite text-base md:text-[17px] leading-[1.9]">
            {block.body}
          </p>
        )}
        {block.list && (
          <ul className="space-y-2 mt-2">
            {block.list.map((li, j) => (
              <li
                key={j}
                className="text-graphite text-base md:text-[17px] leading-[1.7] flex gap-3"
              >
                <span className="text-gold mt-2 inline-block w-2 h-px bg-gold flex-shrink-0" />
                <span>{li}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Reveal>
  );
};

const RichContent = ({ content }) => (
  <div className="grid grid-cols-12 gap-10">
    <div className="col-span-12 md:col-span-8">
      {content.tagline && (
        <Reveal>
          <p className="font-serif italic text-2xl md:text-3xl text-charcoal leading-snug max-w-3xl">
            {content.tagline}
          </p>
        </Reveal>
      )}
      {content.intro && (
        <Reveal delay={120}>
          <div className="mt-10 space-y-6 max-w-2xl">
            {content.intro.map((p, i) => (
              <p key={i} className="text-graphite text-base md:text-lg leading-[1.9]">
                {p}
              </p>
            ))}
          </div>
        </Reveal>
      )}

      {content.sections && content.sections.length > 0 && (
        <div className="mt-14">
          {content.sections.map((b, i) => (
            <BlockSection key={i} block={b} index={i} />
          ))}
        </div>
      )}

      {content.closing && (
        <Reveal>
          <p className="mt-10 font-serif italic text-charcoal text-xl md:text-2xl leading-snug border-t border-stone pt-8 max-w-2xl">
            {content.closing}
          </p>
        </Reveal>
      )}
    </div>
  </div>
);

const FallbackContent = ({ item, section }) => (
  <div className="grid grid-cols-12 gap-10">
    <Reveal className="col-span-12 md:col-span-7">
      <p className="overline mb-6">Présentation</p>
      <p className="font-serif italic text-2xl md:text-3xl text-charcoal leading-snug max-w-3xl">
        {item.teaser}
      </p>
      <p className="mt-10 text-graphite text-base md:text-lg leading-[1.9] max-w-2xl">
        Le contenu détaillé de cette page sera communiqué prochainement. Vous y
        découvrirez nos méthodes d'intervention, nos savoir-faire spécifiques,
        ainsi que des exemples concrets de réalisations.
      </p>
      <p className="mt-6 text-graphite text-base md:text-lg leading-[1.9] max-w-2xl">
        En attendant, n'hésitez pas à explorer les autres spécialités de notre
        univers <span className="italic">{section.label}</span> ou à découvrir
        la Méthode Rozier dans son ensemble.
      </p>
    </Reveal>
  </div>
);

export default function SubPage() {
  const { pathname } = useLocation();
  const { section, item } = findSectionAndItemByPath(pathname);

  if (!section || !item) {
    return (
      <div className="pt-[140px] pb-24 container-rozier">
        <p className="font-serif text-3xl text-charcoal">Page introuvable.</p>
        <Link to="/" className="mt-6 inline-block text-gold underline">
          Retour à l'accueil
        </Link>
      </div>
    );
  }

  const content = SUBPAGE_CONTENT[item.key];
  const siblings = section.items.filter((i) => i.key !== item.key);

  return (
    <div data-testid={`page-sub-${item.key}`}>
      <section className="relative h-[56vh] min-h-[420px] overflow-hidden">
        <img
          src={section.image}
          alt={item.label}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/25 to-black/70" />
        <div className="absolute inset-0 flex items-end">
          <div className="container-rozier pb-14 md:pb-16 w-full">
            <Link
              to={section.path}
              className="inline-flex items-center gap-2 text-white/80 hover:text-gold transition-colors text-xs uppercase tracking-[0.28em] mb-5"
              data-testid="subpage-back"
            >
              <ArrowLeft size={14} />
              {section.label}
            </Link>
            <p className="overline text-white/70 mb-3 sr-only">{section.label}</p>
            <h1 className="font-serif text-white text-4xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight font-light max-w-4xl">
              {item.label}
            </h1>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24 bg-white">
        <div className="container-rozier grid grid-cols-12 gap-10">
          <div className="col-span-12 lg:col-span-8">
            {content ? (
              <RichContent content={content} />
            ) : (
              <FallbackContent item={item} section={section} />
            )}
          </div>

          <Reveal className="col-span-12 lg:col-span-3 lg:col-start-10" delay={150}>
            <div className="border-t border-stone pt-8 lg:sticky lg:top-32">
              <p className="overline mb-4">Autres spécialités</p>
              <ul className="space-y-3">
                {siblings.map((s) => (
                  <li key={s.key}>
                    <Link
                      to={s.path}
                      className="group flex items-center justify-between py-2 border-b border-stone hover:border-gold transition-colors"
                      data-testid={`subpage-sibling-${s.key}`}
                    >
                      <span className="text-charcoal group-hover:text-gold transition-colors font-serif text-lg">
                        {s.label}
                      </span>
                      <ArrowUpRight
                        size={14}
                        className="text-graphite group-hover:text-gold transition-colors"
                      />
                    </Link>
                  </li>
                ))}
              </ul>

              <div className="mt-10">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-3 bg-charcoal text-white px-6 py-3 uppercase tracking-[0.28em] text-[11px] hover:bg-gold transition-colors duration-300"
                  data-testid="subpage-cta-contact"
                >
                  Parlons de votre projet
                  <ArrowUpRight size={12} />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
